import { useEffect, useRef } from "react";
import moment from "moment";
import { DateTimeFormatRepository } from "consts/general";
import { FormAdapter } from "hooks/useForm";
import { convertToBooleanSwitch } from "util/transformers";
import useMerchantDetailsTabContext from "../useMerchantDetailsTabContext";
import type { MerchantDomainsRepository } from "./repository";
import type { MerchantBusiness, MerchantDomain } from "features/merchants/types";
import { useMerchantContext } from "../MerchantProvider";

export default function useMerchantDomains() {
    const merchant = useMerchantContext();
    const merchantRef = useRef(merchant);
    merchantRef.current = merchant;

    const repository = useMerchantDetailsTabContext<MerchantDomainsRepository>();
    const repositoryRef = useRef(repository);
    repositoryRef.current = repository;

    const createMerchantBusiness = async ({ businessName }: Partial<MerchantBusiness>) => {
        const response = await repository.createMerchantBusiness({
            businessName: String(businessName),
            merchantId: merchant.coreId
        });

        return FormAdapter.fromResponse(response);
    };

    const createMerchantDomain = async ({
        merchantBusinessId,
        sourceUrl,
        expiresAtDate
    }: Partial<MerchantDomain>) => {
        const response = await repository.createMerchantDomain({
            expiresAtDate: moment(expiresAtDate)
                .format(DateTimeFormatRepository.Default),
            merchantBusinessId: String(merchantBusinessId),
            sourceUrl: String(sourceUrl)
        });

        return FormAdapter.fromResponse(response);
    };

    const deleteMerchantBusiness = async (merchantBusiness: MerchantBusiness) => (
        repository.deleteMerchantBusiness(merchantBusiness)
    );

    const deleteMerchantDomain = async (merchantDomain: MerchantDomain) => (
        repository.deleteMerchantDomain(merchantDomain)
    );

    const toggleEnabledMerchantBusiness = (
        merchantBusiness:
            & Omit<Partial<MerchantBusiness>, 'isEnabled'>
            & {
                isEnabled: boolean;
            }
    ) => (
        repository.updateMerchantBusiness({
            ...merchantBusiness,
            isEnabled: convertToBooleanSwitch(merchantBusiness.isEnabled)
        }, {
            shouldNotifyOnSuccess: true,
            onSuccess: () => repository.fetchMerchantDomains(merchantRef.current)
        })
    );

    const updateMerchantBusiness = async (merchantBusiness: Partial<MerchantBusiness>) => (
        FormAdapter.fromResponse(
            await repository.updateMerchantBusiness(merchantBusiness, { shouldNotifyOnSuccess: false })
        )
    );

    const updateMerchantDomain = async ({ expiresAtDate, ...restMerchantDomain }: Partial<MerchantDomain>) => (
        FormAdapter.fromResponse(
            await repository.updateMerchantDomain({
                ...restMerchantDomain,
                expiresAtDate: moment(expiresAtDate)
                    .format(DateTimeFormatRepository.Default)
            })
        )
    );

    const getMerchantDomainsByBusinessId = (merchantBusinessId: MerchantBusiness['coreId']) => (
        repository.getMerchantDomainsByBusinessId(merchantBusinessId)
    );

    const getMerchantBusinesses = () => (
        repository.merchantBusinesses
    );

    const fetchMerchantBusinesses = () => (
        repository.fetchMerchantBusinesses()
    );

    const fetchMerchantBusinessById = (merchantBusinessId: MerchantBusiness['coreId']) => (
        repository.fetchMerchantBusinessById(merchantBusinessId)
    );

    useEffect(() => {
        repositoryRef.current
            .initMerchantDomains(merchantRef.current);
    }, []);

    return {
        repository,
        fetchMerchantBusinesses,
        fetchMerchantBusinessById,
        getMerchantDomainsByBusinessId,
        getMerchantBusinesses,
        createMerchantBusiness,
        updateMerchantBusiness,
        updateMerchantDomain,
        toggleEnabledMerchantBusiness,
        createMerchantDomain,
        deleteMerchantBusiness,
        deleteMerchantDomain
    };
};
